import { ChangeDetectionStrategy, Component, computed, input, Input, ViewEncapsulation } from '@angular/core';
import { bindAttribute, bindBooleanAttribute } from '@bynary/composables/attribute';
import { bindModifier, provideBaseClass, useModifierGroup } from '@bynary/composables/class';
import { EnumLike } from '@sona/shared-model-common';

import { SonaSpinnerComponent } from '../spinner/spinner.component';

import { ButtonAppearance } from './models/button-appearance.enum';
import { ButtonColor } from './models/button-color.enum';

/**
 * Button component for clicking things
 */
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'button[sonaButton], a[sonaButton]',
    imports: [SonaSpinnerComponent],
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideBaseClass('c-sona-button')]
})
export class SonaButtonComponent {
    readonly disabled = input(false);
    readonly isLoading = input(false);
    private readonly _color = useModifierGroup<
        EnumLike<ButtonColor> | undefined
    >(undefined, { prefix: 'color' });
    private readonly _appearance = useModifierGroup<EnumLike<ButtonAppearance>>(
        ButtonAppearance.SOLID
    );

    constructor() {
        bindBooleanAttribute('disabled', this.disabled);
        bindAttribute(
            'tabindex',
            computed(() => (this.disabled() ? '-1' : '0'))
        );
        bindModifier('is-loading', this.isLoading);
    }

    /**
     * The appearance of the button
     */
    @Input()
    get appearance(): EnumLike<ButtonAppearance> {
        return this._appearance();
    }

    set appearance(value: EnumLike<ButtonAppearance>) {
        this._appearance.set(value);
    }

    /**
     * The color of the button
     */
    @Input()
    get color(): EnumLike<ButtonColor> | undefined {
        return this._color();
    }

    set color(value: EnumLike<ButtonColor> | '' | undefined) {
        this._color.set(value || undefined);
    }
}
